import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  IconButton,
  Stack,
  Typography,
  useMediaQuery,
  Container,
  Paper,
  Step,
  StepButton,
  StepLabel,
  Stepper
} from '@mui/material'
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos'
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos'
import React, { useEffect, useState, useRef, useContext } from 'react'
import { FormProvider, useWatch } from 'react-hook-form'

import ArrowDownIcon from '@mui/icons-material/ArrowDownward'
import ArrowUpIcon from '@mui/icons-material/ArrowUpward'
import ErrorIcon from '@mui/icons-material/Error'
import ReportIcon from '@mui/icons-material/Report'
import { LoadingButton } from '@mui/lab'
import { observer } from 'mobx-react'
import moment from 'moment'
import { useSnackbar } from 'notistack'
import { useHistory, useParams } from 'react-router-dom'
import { StringsContext, useStrings } from '../../assets/localization/strings'
import { CampaignGender, Country, ProductType, Service, StylinkGender, VideoType } from '../../models/GeneralTypes'
import { numberToEuro } from '../../reusableUtils/Helpers'
import { PricingContext, usePricing } from '../../reusableUtils/pricing'
import { ReadonlyContext } from '../../reusableUtils/useReadonly'
import rootStore from '../../stores/rootStore'
import { color, theme } from '../../theme'
import { MockView } from '../common/MockView'
import PaymentModal from '../common/PaymentModal'
import CampaignAdditionalFeatures from './CampaignAdditionalFeatures'
import CampaignAudience from './CampaignAudience'
import CampaignBudget from './CampaignBudget'
import CampaignProduct from './CampaignProduct'
import CampaignTask from './CampaignTask'
import CampaignType from './CampaignType'
import { PricingFormContext, PricingInUGCForm, usePricingFormByContext } from './Pricing'
import usePersistedUGCForm, { IUGCCampaign, ageOptionToObj } from './usePersistedUGCForm'
import { getVideoUrl } from '../../config'
import CampaignImages from './CampaignImages'
import { getCurrencySymbolByCountry } from '../../reusableUtils/currency'
import CustomStepIcon from './custom_step_icon'
import CustomConnector from './custom_step_connector'
import CampaignPreferences from './CampaignPreferences'
import CampaignDisclaimer from './CampaignDisclaimer'
import ReactPlayer from 'react-player'

export type { IUGCCampaign }

export type Creator = Awaited<ReturnType<typeof rootStore.campaignStore.getAllFinishedCreators>>[number]

const mobilePricingHeight = '40vh'

const UGCCampaignForm = observer(() => {
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const { enqueueSnackbar } = useSnackbar()
  const history = useHistory()
  const { language } = useContext(StringsContext)
  const [showMobilePricing, setShowMobilePricing] = (() => {
    const [showMobilePricing, setShowMobilePricing] = useState(false)
    return [isMobile && showMobilePricing, setShowMobilePricing]
  })()

  const [showPaymentModal, setShowPaymentModal] = useState(false)
  const [loading, setLoading] = useState(false)
  const [submitLoading, setSubmitLoading] = useState(false)

  const { selectedBrand } = rootStore.brandStore
  const { brandId, campaignId, type } = useParams<{ brandId: string; campaignId: string; type: 'update' | 'duplicate' | 'view'; couponCode?: string }>()
  const isAgency = selectedBrand?.isAgency ?? false
  const isCreation = !campaignId
  const id = type === 'duplicate' ? `${brandId}_${campaignId}_duplicate` : isCreation ? brandId : `${brandId}_${campaignId}`

  const { dialogMethods, methods, loadIntoForm, getUgcObjectFromForm, compareWithPersistenceAndAsk } = usePersistedUGCForm(id, isAgency, !selectedBrand?.brandLogo)
  const { pricing } = usePricing()
  const [brand, setBrand] = useState<any>()
  const strings = useStrings()
  const pricingForm = usePricingFormByContext(methods.control, showPaymentModal ? brand : undefined)
  const noPayment = useWatch({ control: methods.control, name: 'noPaymentCheck' })

  const [creators, setCreators] = (() => {
    const [creators, setCreators] = useState<Creator[]>()
    const gender = useWatch({ control: methods.control, name: 'gender' })
    const ageOptions = useWatch({ control: methods.control, name: 'ageRanges' })
    const filtered = Array.isArray(creators)
      ? creators.filter(
        c =>
          (`${gender}` === CampaignGender.ANY || `${gender}` === c.gender) &&
          ageOptions.find(o => {
            const age = moment().diff(c.birth_date, 'y', true)
            return ageOptionToObj[o].min <= age && age <= ageOptionToObj[o].max
          })
      )
      : creators
    return [filtered, setCreators]
  })()

  const isCreating = !!(brandId && campaignId)
  const isReadonly = type === 'update' || type === 'view'
  const isUnboxing = methods.getValues('videoType') === VideoType.unboxing

  const wizardSteps = [
    {
      label: strings.campaign_creation_section_product,
      content: (
        <Box>
          <CampaignProduct />
        </Box>
      ),
      fieldNames: ['productType', 'name', 'price', 'link', 'industry', 'description', 'campaignImage'],
      videoUrl: 'https://s3.eu-central-1.amazonaws.com/com.freebeeapp/business-webapp/Product_1.mov',
      videoHeadline: strings.tutorial_one
    },
    {
      label: 'Video',
      content: (
        <Box>
          <>
            <CampaignType />
            <CampaignBudget />
          </>
        </Box>
      ),
      fieldNames: ['videoType', 'videoDuration', 'videoFormat', 'numberOfVideos'],
      videoUrl: 'https://s3.eu-central-1.amazonaws.com/com.freebeeapp/business-webapp/Video_2.mov',
      videoHeadline: strings.tutorial_two
    },
    {
      label: 'Content',
      content: (
        <Box>
          <CampaignTask />
          {isUnboxing && <CampaignAudience />}
        </Box>
      ),
      fieldNames: ['service', 'scenes'],
      videoUrl: 'https://s3.eu-central-1.amazonaws.com/com.freebeeapp/business-webapp/Content_3.mov',
      videoHeadline: strings.tutorial_three
    },
    {
      label: 'Add ons',
      content: (
        <Box>
          {isUnboxing ? (
            <CampaignImages isReadOnly={isReadonly} />
          ) : (
            <>
              <CampaignAdditionalFeatures />
              <CampaignImages isReadOnly={isReadonly} />
            </>
          )}
        </Box>
      ),
      fieldNames: ['photos'],
      videoUrl: 'https://s3.eu-central-1.amazonaws.com/com.freebeeapp/business-webapp/Add_on_4.mov',
      videoHeadline: strings.tutorial_four
    },
    {
      label: 'Creator',
      content: (
        <Grid container sx={{ borderRadius: '16px', borderStyle: 'solid', borderColor: '#0000001A', p: '12px' }}>
          <Box>
            <CampaignAudience />
            <CampaignPreferences />
          </Box>
        </Grid>
      ),
      fieldNames: ['gender', 'language'],
      videoUrl: 'https://s3.eu-central-1.amazonaws.com/com.freebeeapp/business-webapp/Creator_5.mov',
      videoHeadline: strings.tutorial_five
    },
    {
      label: strings.brand_settings_nav_payment,
      content: (
        <Box>
          <CampaignDisclaimer />
        </Box>
      ),
      fieldNames: [''],
      videoUrl: 'https://s3.eu-central-1.amazonaws.com/com.freebeeapp/business-webapp/Payment_6.mov',
      videoHeadline: strings.tutorial_six
    }
  ]
  // If isUnboxing is true, filter out 'Add ons' and 'Creator' steps.
  const filteredWizardSteps = wizardSteps
  const [activeStep, setActiveStep] = useState(0)
  const contentRef = useRef(null)

  useEffect(() => {
    checkIfUpdate()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  useEffect(() => {
    if (contentRef.current) contentRef.current.scrollTo({ top: 0, behavior: 'smooth' })
  }, [activeStep])

  useEffect(() => {
    pricingForm.data.brand = brand
  }, [brand, pricingForm.data])

  const goToDashboard = () => history.push(`/${brandId}/dashboard`)

  const loadCreators = async () => {
    if (isReadonly) return
    setCreators(undefined)
    try {
      const finishedContent = await rootStore.campaignStore.getAllFinishedCreators()
      setCreators(finishedContent)
    } catch (e) {
      setCreators(e)
    }
  }

  const checkIfUpdate = async () => {
    if (isCreating) {
      if (type !== 'update' && type !== 'duplicate' && type !== 'view') {
        goToDashboard()
        return
      }
      setLoading(true)
      try {
        const campaign = await rootStore.campaignStore.getUgcCampaign(campaignId, brandId)
        if (!campaign) {
          goToDashboard()
          return
        }
        await loadIntoForm(campaign)
      } catch (e) {
        console.error(e)
        enqueueSnackbar(strings.error, { variant: 'error' })
        goToDashboard()
      } finally {
        setLoading(false)
      }
    }
    loadCreators()
    if (type !== 'view') compareWithPersistenceAndAsk()
  }

  const onSubmit = async () => {
    if (!selectedBrand) {
      enqueueSnackbar(strings.error, { variant: 'error' })
      return
    }
    try {
      setSubmitLoading(true)
      const ugcCampaign = await getUgcObjectFromForm(selectedBrand, pricingForm.couponCode)
      ugcCampaign.numberOfVideos = +ugcCampaign.numberOfVideos
      ugcCampaign.numberOfPhotos = +ugcCampaign.numberOfPhotos
      let campaign: {
        brandId: string
        campaignId: string
      }
      if (type === 'update') {
        await rootStore.campaignStore.updateCampaignUGC({
          ...ugcCampaign,
          moodboard: ugcCampaign.moodboard ?? [],
          campaignId
        })
        enqueueSnackbar(strings.campaign_updated_succesfully, { variant: 'success' })
      } else {
        if (methods.getValues('service') !== Service.selfService && methods.getValues('videoType') === VideoType.ad) {
          ugcCampaign.scenes = []
        }
        if (selectedBrand && !selectedBrand.brandLogo && !selectedBrand.isAgency) {
          selectedBrand.brandLogo = ugcCampaign.brandLogo
        }
        campaign = await rootStore.campaignStore.createUgcCampaign({
          ...ugcCampaign,
          status: noPayment ? 'published' : 'draft'
        })
        noPayment && enqueueSnackbar(strings.campaign_created_succesfully, { variant: 'success' })
      }

      dialogMethods.deletePersistence()
      return { brandId: selectedBrand.brandId, campaignId: campaign?.campaignId || campaignId }
    } catch (e) {
      console.log(e)
      enqueueSnackbar(strings.error, { variant: 'error' })
    } finally {
      setSubmitLoading(false)
    }
  }

  if (pricing == null)
    return (
      <Grid container justifyContent='center'>
        <CircularProgress />
      </Grid>
    )
  if (pricing instanceof Error) return <>{strings.error}</>

  type PersistenceDialogConfig = ReturnType<typeof usePersistedUGCForm>['dialogMethods']
  const PersistenceDialog = ({ showPersistDialog, setDataFromPersistence, closePersistDialog, deletePersistence }: PersistenceDialogConfig) => {
    const strings = useStrings()
    return (
      <Dialog open={showPersistDialog} onClose={closePersistDialog}>
        <DialogTitle>{strings.campaign_creation_persist_title}</DialogTitle>
        <DialogContent>
          <DialogContentText>{strings.campaign_creation_persist_body}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button sx={{ background: '#000', color: 'white' }} onClick={setDataFromPersistence}>
            {strings.yes}
          </Button>
          <Button onClick={deletePersistence}>{strings.campaign_creation_persist_delete}</Button>
        </DialogActions>
      </Dialog>
    )
  }

  const renderPricingPortal = () => <PricingInUGCForm mobile={isMobile} createCampaign={onSubmit} noPayment={noPayment} checkout={activeStep === filteredWizardSteps.length - 1} />

  const hasErrors = Object.keys(methods.formState.errors).length > 0
  const fields: (keyof IUGCCampaign)[] = ['videoType', 'unboxingType', 'videoDuration', 'videoFeature', 'numberOfVideos', 'service', 'hooks', 'ctas', 'productUrls']
  const price = pricingForm.invoice ? (
    pricingForm.invoice instanceof Error ? (
      <IconButton onClick={() => pricingForm.load(0)}>
        <ErrorIcon />
      </IconButton>
    ) : (
      numberToEuro(pricingForm.invoice.subtotal)
    )
  ) : (
    <CircularProgress size={20} sx={{ mr: 1 }} />
  )

  const currencySymbol = rootStore.brandStore.selectedBrand?.brandCountry ? getCurrencySymbolByCountry(rootStore.brandStore.selectedBrand?.brandCountry) : '€'

  return (
    <ReadonlyContext.Provider
      value={{
        mode: type === 'update' ? 'some' : type === 'view' ? 'all' : 'none',
        names: type === 'update' ? fields : undefined
      }}>
      <PricingFormContext.Provider value={pricingForm}>
        <PricingContext.Provider value={pricing}>
          <MockView name='Create Campaign'>
            <Grid container>
              <Grid item xs={4}>
                <Button
                  onClick={() => {
                    const empty = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAAAAAA6fptVAAAACklEQVQYV2P4DwABAQEAWk1v8QAAAABJRU5ErkJggg=='
                    methods.setValue('name', 'infs')
                    methods.setValue('price', 200)
                    methods.setValue('campaignImage', empty)
                    methods.setValue('link', 'google.com')
                    methods.setValue('description', 'desc')
                    methods.setValue('productType', ProductType.physical)

                    methods.setValue('brandName', 'brand infs')
                    methods.setValue('brandLogo', empty)

                    methods.setValue('scenes', [{ id: 'id1', value: 'sc1' }])
                    methods.setValue('informationCheck', true)
                    methods.setValue('contactCheck', true)
                  }}>
                  Fill out
                </Button>
              </Grid>
              <Grid item xs={4}>
                <Button
                  onClick={() => {
                    const link = 'https://s3.eu-central-1.amazonaws.com/com.freebeeapp/dog.mp4'
                    setCreators([
                      {
                        birth_date: moment().add(-20, 'y').toDate(),
                        gender: StylinkGender.DIVERSE,
                        link,
                        first_name: 'name1',
                        influencerId: 'id1'
                      },
                      {
                        birth_date: moment().add(-20, 'y').toDate(),
                        gender: StylinkGender.MALE,
                        link,
                        first_name: 'name2',
                        influencerId: 'id2'
                      },
                      {
                        birth_date: moment().add(-20, 'y').toDate(),
                        gender: StylinkGender.FEMALE,
                        link,
                        first_name: 'name3',
                        influencerId: 'id3'
                      }
                    ])
                  }}>
                  Set Creators
                </Button>
              </Grid>
              <Grid item xs={4}>
                <Button onClick={() => setCreators([])}>Clear Creators</Button>
              </Grid>
              <Grid item xs={4}>
                <Button onClick={() => setCreators(undefined)}>Set Creators Loading</Button>
              </Grid>
              <Grid item xs={4}>
                {methods.getValues('inviteInfluencers').join(', ')}
              </Grid>
              <Grid item xs={4}>
                {methods.getValues('videoFeature')}
              </Grid>
              <Grid item xs={4}>
                Locale {methods.getValues('locale')}
              </Grid>
            </Grid>
          </MockView>
          <PersistenceDialog {...dialogMethods} />
          {!isReadonly && (
            <PaymentModal
              open={showPaymentModal}
              handleClose={() => {
                pricingForm.ignoreLoad.current = true
                setShowPaymentModal(false)
              }}
              campaignForm={methods}
              paymentType='UGC_CAMPAIGN'
              setBrand={setBrand}
              callback={onSubmit}
            />
          )}
          <FormProvider {...methods}>
            <form
              style={{ display: 'flex', justifyContent: 'center' }}
              onSubmit={methods.handleSubmit(() => {
                if (hasErrors) return
                if (type === 'update') {
                  onSubmit()
                  return
                }
                pricingForm.ignoreLoad.current = true
                setShowPaymentModal(true)
              })}>
              <Grid
                sx={{
                  height: 'calc(100vh - 112px)',
                  position: 'relative',
                  background: '#FDFDFD',
                  maxWidth: '1400px',
                  width: '100%',
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'space-between'
                }}>
                <div
                  style={{
                    position: 'relative',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'flex-start',
                    alignItems: 'center',
                    width: '70%',
                    minHeight: '80vh',
                    borderRadius: '16px',
                    marginRight: '4px',
                    paddingLeft: '4px',
                    paddingRight: '4px',
                    backgroundColor: '#ffffff',
                    boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)'
                  }}>
                  {' '}
                  <div
                    style={{
                      position: 'sticky',
                      top: 0,
                      width: '100%'
                    }}>
                    <Box sx={{ textAlign: 'left', width: '90%', mt: '18px' }}>
                      <Typography variant='h3' sx={{ fontSize: '24px', fontWeight: 500, color: '#190851', fontFamily: 'Bricolage Grotesque', ml: '32px' }}>
                        {type === 'update' ? strings.edit_order : type === 'view' ? strings.view_order : strings.create_order}
                      </Typography>
                    </Box>
                    {/* Wizard Stepper Header */}
                    <Container maxWidth='lg' sx={{ pt: 1, ml: 0 }}>
                      <Paper sx={{ px: 2, py: 1 }}>
                        <Stepper activeStep={activeStep} alternativeLabel connector={<CustomConnector />}>
                          {filteredWizardSteps.map((step, index) => (
                            <Step key={index}>
                              {index < activeStep ? (
                                <StepButton
                                  sx={{
                                    '&:active': { backgroundColor: 'transparent' },
                                    '&:focus': { backgroundColor: 'transparent' },
                                    '&.Mui-focusVisible': { backgroundColor: 'transparent' }
                                  }}
                                  onClick={() => setActiveStep(index)}>
                                  <StepLabel
                                    StepIconComponent={CustomStepIcon}
                                    sx={{
                                      '& .MuiStepLabel-label': {
                                        color: '#a890fe !important',
                                        fontSize: '14px',
                                        fontWeight: '700 !important'
                                      }
                                    }}>
                                    {step.label}
                                  </StepLabel>
                                </StepButton>
                              ) : (
                                <StepLabel
                                  StepIconComponent={CustomStepIcon}
                                  sx={{
                                    '& .MuiStepLabel-label': {
                                      color: '#a890fe !important',
                                      fontSize: '14px',
                                      fontWeight: '700 !important'
                                    }
                                  }}>
                                  {step.label}
                                </StepLabel>
                              )}
                            </Step>
                          ))}
                        </Stepper>
                      </Paper>
                    </Container>
                  </div>
                  {/* Wizard Content */}
                  <Grid
                    ref={contentRef}
                    xs={12}
                    sx={{
                      flex: 1,
                      overflowY: 'auto',
                      paddingBottom: '50px',
                      margin: '0 auto',
                      [theme.breakpoints.down('lg')]: { marginLeft: 0 },
                      [theme.breakpoints.down('md')]: { marginRight: 0 },
                      [theme.breakpoints.up('sm')]: { padding: '25px' },
                      textAlign: loading ? 'center' : 'start',
                      pb: showMobilePricing ? mobilePricingHeight : undefined
                    }}>
                    {loading ? (
                      <CircularProgress />
                    ) : (
                      <Box
                        sx={{
                          width: '100%',
                          textAlign: 'start',
                          color: '#190851', // this sets all text color of the steps to #190851
                          '& *': { color: 'inherit !important' } // this sets all text color of the steps to #190851
                        }}>
                        {filteredWizardSteps[activeStep].content}
                      </Box>
                    )}
                  </Grid>
                  <div
                    style={{
                      position: 'sticky',
                      bottom: 0,
                      width: '90%',
                      marginTop: '16px',
                      marginBottom: '16px',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      zIndex: 1000
                    }}>
                    {activeStep > 0 ? (
                      <Button
                        style={{
                          backgroundColor: '#7766B31A',
                          borderRadius: '10px',
                          textAlign: 'center',
                          color: '#7766B3',
                          textTransform: 'none',
                          fontSize: '16px',
                          fontWeight: 600,
                          paddingLeft: '18px',
                          paddingRight: '18px'
                        }}
                        onClick={() => setActiveStep(prev => prev - 1)}>
                        <ArrowBackIosIcon sx={{ fontSize: 16, fontWeight: 600, marginRight: '2px' }} />
                        <span>{strings.back}</span>
                      </Button>
                    ) : (
                      // Placeholder element with fixed width to take up the space of the back button
                      <div style={{ width: '100px' }} />
                    )}
                    <Button
                      style={{
                        display: activeStep === filteredWizardSteps.length - 1 && type !== 'update' ? 'none' : 'flex',
                        backgroundColor: '#a890fe',
                        borderRadius: '10px',
                        textAlign: 'center',
                        color: '#ffffff',
                        textTransform: 'none',
                        fontSize: '16px',
                        fontWeight: 600,
                        paddingLeft: '18px',
                        paddingRight: '18px'
                      }}
                      id={type === 'update' ? 'UPDATE_CAMPAIGN_FORM' : 'CREATE_CAMPAIGN_FORM'}
                      onClick={async e => {
                        // If we're not on the final step, validate before advancing.
                        if (activeStep < filteredWizardSteps.length - 1) {
                          e.preventDefault() // prevent form submission
                          const isValid = await methods.trigger(filteredWizardSteps[activeStep].fieldNames)
                          if (isValid) setActiveStep(prev => prev + 1)
                        }
                        if (activeStep === filteredWizardSteps.length - 1 && type === 'update') {
                          e.preventDefault()
                          onSubmit()
                          history.push(`/${brandId}/dashboard`, { refresh: true })
                        }
                      }}>
                      {activeStep === filteredWizardSteps.length - 1 && type === 'update' ? (
                        <span>{strings.save}</span>
                      ) : (
                        <>
                          <span>{strings.next}</span>
                          <ArrowForwardIosIcon sx={{ fontSize: 16, fontWeight: 600, marginLeft: '2px' }} />
                        </>
                      )}
                    </Button>
                  </div>
                  {hasErrors && (
                    <>
                      <ReportIcon fontSize='small' color='error' />
                      <Typography variant='caption' color='error' pl={1} pr={2} my='8px'>
                        {strings.campaign_creation_error}
                      </Typography>
                    </>
                  )}
                </div>
                {/* Pricing Portal for non-mobile, non-payment modal */}
                {!isReadonly && !showPaymentModal && !isMobile && (
                  <Grid
                    sx={{
                      overflowY: 'auto',
                      width: '30%',
                      borderRadius: '16px',
                      ml: '8px',
                      px: '24px',
                      pt: '24px',
                      backgroundColor: activeStep === filteredWizardSteps.length - 1 ? '#AB95FF1A' : '#ffffff',
                      boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)'
                    }}>
                    {renderPricingPortal()}
                    {selectedBrand?.country?.toLocaleLowerCase() !== Country.great_britain && language !== 'en' && (
                      <Grid xs={12} style={{ display: 'flex', justifyContent: ' center', justifyItems: 'center', borderRadius: '10px' }}>
                        <Box sx={{ width: '368px', textAlign: 'center' }}>
                          <Typography
                            variant='body2'
                            sx={{
                              fontWeight: 100,
                              mb: 2,
                              fontSize: '16px',
                              color: '#190851',
                              fontFamily: 'Bricolage Grotesque'
                            }}>
                            {filteredWizardSteps[activeStep].videoHeadline}
                          </Typography>
                          <span className='roundedVideo' style={{ borderRadius: '8px 8px 0 0' }}>
                            <ReactPlayer
                              url={filteredWizardSteps[activeStep].videoUrl}
                              height='400px'
                              width='368px'
                              controls={true}
                              style={{ marginLeft: 'auto', marginRight: 'auto', marginTop: '5px', borderRadius: '8px 8px 0 0' }}
                            />
                          </span>
                        </Box>
                      </Grid>
                    )}
                  </Grid>
                )}

                {/* Wizard Navigation & Bottom Bar */}
                <Stack
                  sx={{
                    display: isMobile ? 'block' : 'none',
                    width: '100%',
                    position: 'fixed',
                    bottom: 0,
                    left: 0,
                    overflowX: 'hidden',
                    zIndex: 10,
                    backgroundColor: color.white,
                    padding: '5px',
                    borderTop: '1px solid #e0e0e0',
                    maxHeight: mobilePricingHeight
                  }}>
                  {showMobilePricing && !showPaymentModal && renderPricingPortal()}
                  <Box pb={5} />
                  <Stack
                    direction='row'
                    alignItems='center'
                    sx={{
                      width: '100%',
                      position: 'fixed',
                      bottom: 0,
                      left: 0,
                      backgroundColor: color.white,
                      padding: '5px',
                      borderTop: '1px solid #e0e0e0',
                      zIndex: 5
                    }}>
                    {isMobile && <IconButton onClick={() => setShowMobilePricing(p => !p)}>{showMobilePricing ? <ArrowDownIcon /> : <ArrowUpIcon />}</IconButton>}
                    {isMobile && (
                      <>
                        {typeof price !== 'string' && (
                          <Typography component='div' variant='h5' whiteSpace='nowrap' sx={{ mr: 1 }}>
                            {`${currencySymbol} `}
                          </Typography>
                        )}
                        {typeof price === 'string' ? (
                          <Typography component='div' variant='h5' whiteSpace='nowrap' sx={{ mr: 1 }}>
                            {`${currencySymbol} ${price}`}
                          </Typography>
                        ) : (
                          price
                        )}
                      </>
                    )}
                  </Stack>
                </Stack>
              </Grid>
            </form>
          </FormProvider>
        </PricingContext.Provider>
      </PricingFormContext.Provider>
    </ReadonlyContext.Provider>
  )
})

export default UGCCampaignForm
